import { useQuery } from '@apollo/client';
import { GeoJsonLayerProps } from '@deck.gl/layers/typed';
import { Box, DialogActions, DialogContent, Skeleton, styled } from '@mui/material';
import { captureException } from '@sentry/react';
import {
  circle,
  Feature,
  FeatureCollection,
  featureCollection,
  geometry,
  multiPolygon,
  MultiPolygon,
  Point,
  pointOnFeature,
  polygon,
  Polygon,
} from '@turf/turf';
import FlexBox from 'Components/FlexBox';
import { DialogDefault, ThemeButton, ThemeTypography } from 'DesignSystem';
import Image from 'DesignSystem/DataDisplay/Image/Image';
import GeoDataField from 'DesignSystem/Formik/GeoDataField/GeoDataField';
import { ICustomMapRef, IMapPopup } from 'DesignSystem/Map/CustomMap';
import { radiusFromHectares, toMapFeature } from 'DesignSystem/Map/utils/map.utils';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import PlotInfoPopup, { IFeatureProperties } from 'FeatureModules/Site/Components/PlotInfoPopup';
import usePlotsMutations from 'FeatureModules/Site/hooks/usePlotMutations';
import { IPlotsValues, plotsSchema } from 'FeatureModules/Site/schemas/sites.schema';
import { IPlot, TGeometry } from 'FeatureModules/Site/types/plot.types';
import { ESiteType, IBaseFarmSite, IFarmSite } from 'FeatureModules/Site/types/site.types';
import { SiteTypeLabels } from 'FeatureModules/Site/utils/site.utils';
import { Field, Form, Formik, FormikProps } from 'formik';
import { GET_NEIGHBOURING_PLOTS, GET_PLOTS } from 'graphql/secure/queries/plot.queries';
import {
  INeighbouringPlotsInput,
  IPlotsFilters,
} from 'graphql/secure/queries/types/plot-query.types';
import useDialog from 'hooks/useDialog';
import useMessages from 'hooks/useMessages';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import theme from 'styles/theme';
import { IGraphQlConnection } from 'types/core.types';
import { IDefaultDialogProps } from 'types/dialog.types';
import { convertHexToRGBarray } from 'utils/color.utils';
import { mergeDefaults } from 'utils/form.utils';
import { removeGraphConnections } from 'utils/graphql.utils';
import { v4 as uuid } from 'uuid';

interface IAddEditFarmPlotsDialogProps extends IDefaultDialogProps {
  farm: IBaseFarmSite;
  /**
   * Default view of the dialog, can be 'map' or 'json'
   * @default is 'map'
   */
  defaultView?: 'map' | 'json';
  /**
   * Dataset ID to be used to only show neighboring plots that are in the same dataset
   */
  geoDataId?: string;
  /**
   * Callback function to be called after the plots were updated
   */
  onPlotsUpdated?: () => void;
}

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  display: 'flex',
  padding: theme.spacing(3),
  flexDirection: 'column',
  alignItems: 'normal',
  height: '80vh',
  minWidth: 600,
  width: '100%',
  gap: theme.spacing(2),
}));

const InfoContainer = styled(FlexBox)(({ theme }) => ({
  padding: theme.spacing(1),
  borderRadius: '6px',
  border: `1px solid ${theme.custom.themeColors.primary[40]}`,
  background: theme.custom.themeColors.primary[5],
}));

const TitleContainer = styled(FlexBox)(({ theme }) => ({
  width: '100%',
  padding: '8px',
  alignItems: 'center',
  borderRadius: 6,
  gap: theme.spacing(1),
  backgroundColor: theme.custom.themeColors.primary[5],
  marginBottom: theme.spacing(1),
}));

/**
 * If the site plots already present they will be used like they are
 * Otherwise the plots will be queried from the farm
 */
const AddEditFarmPlotsDialog: FC<IAddEditFarmPlotsDialogProps> = ({
  open,
  farm,
  geoDataId,
  defaultView,
  onPlotsUpdated,
  onClose,
}) => {
  const { data, loading } = useQuery<
    { plots: IGraphQlConnection<IPlot> },
    { filters?: IPlotsFilters }
  >(GET_PLOTS, {
    variables: { filters: { farmIds: [farm.id] } },
    skip: !farm.id,
  });

  const { data: neighbouringPlotsData, loading: neighbouringPlotsLoading } = useQuery<
    { neighbouringPlots: IGraphQlConnection<IPlot> },
    { input?: INeighbouringPlotsInput }
  >(GET_NEIGHBOURING_PLOTS, {
    variables: { input: { geoDataId, farmId: farm.id } },
    skip: !farm.id && !geoDataId,
  });

  const { setErrorMessage, setSuccessMessage } = useMessages();
  const { updatePlots, deletePlots, updateLoading, deleteLoading } = usePlotsMutations();
  const { openDialog, closeDialog } = useDialog();
  const formRef = useRef<FormikProps<IPlotsValues> | null>(null);
  const customMapRef = useRef<ICustomMapRef>(null);

  const [infoPopup, setInfoPopup] = useState<IMapPopup>();

  const plots: IPlot[] = useMemo(() => {
    if (data?.plots) {
      return removeGraphConnections(data.plots);
    }
    return [];
  }, [data]);

  const neighbouringPlots: IPlot[] = useMemo(() => {
    if (neighbouringPlotsData?.neighbouringPlots) {
      return removeGraphConnections(neighbouringPlotsData.neighbouringPlots);
    }
    return [];
  }, [neighbouringPlotsData]);

  const initialValues: IPlotsValues = mergeDefaults(
    plotsSchema.default(),
    plots.length
      ? {
          featureCollection: featureCollection<Polygon | MultiPolygon | Point, { plotId?: string }>(
            plots.map(plot => toMapFeature(plot.feature.geometry, plot.id, plot.externalId))
          ),
        }
      : {
          featureCollection: featureCollection<Polygon | MultiPolygon | Point, { plotId?: string }>(
            []
          ),
        }
  );

  /**
   * This feature collections contains all the cultivation areas of the farm as single polygons with their properties
   * To be able to show the info popup when clicking on the cultivation area on a single polygon we need to have the center of each polygon
   * To be able to show centered markers of the one cultivation area we need to have the center of the multi polygon
   */
  const neighbouringPolygonFeatures: FeatureCollection<
    Polygon | Point | MultiPolygon,
    IFeatureProperties
  > = useMemo(() => {
    const features: Feature<Polygon | Point | MultiPolygon, IFeatureProperties>[] =
      neighbouringPlots.map(plot => {
        const feature = plot.feature;
        const featureGeometry = geometry(feature.geometry.type, feature.geometry.coordinates);
        const properties: IFeatureProperties = {
          plot,
          // Center of the polygon always inside the polygon
          center:
            feature.geometry.type === 'Point'
              ? feature.geometry.coordinates
              : (pointOnFeature(featureGeometry).geometry.coordinates as [number, number]),
          isNeighbouringPlot: true,
        };
        if (feature.geometry.type === 'Polygon') {
          return polygon(feature.geometry.coordinates, properties, { id: uuid() });
        } else if (feature.geometry.type === 'MultiPolygon') {
          return multiPolygon(feature.geometry.coordinates, properties, { id: uuid() });
        } else if (feature.geometry.type === 'Point') {
          return circle(
            feature.geometry.coordinates,
            // For now we use a circle with a radius of 112.8 meters which has 4ha area
            plot.sizeHa ? radiusFromHectares(plot.sizeHa) : 112.8,
            {
              units: 'meters',
              properties,
            }
          );
        }
        throw new Error(
          `Plot with ID ${plot.id} has an invalid geometry type: ${feature.geometry}`
        );
      });
    return featureCollection(features);
  }, [neighbouringPlots]);

  const handleClose = () => {
    if (formRef.current && !isEqual(formRef.current.initialValues, formRef.current.values)) {
      openDialog({
        type: 'ALERT',
        props: {
          title: 'Unsaved changes',
          text: 'Are you sure you want to close this window? All unsaved changes will be lost and you will not be able to undo this action.',
          submitText: 'Discard changes',
          itemTitle: farm.title,
          displayCloseButton: true,
          onSubmit: onClose,
          onCancel: () => undefined,
        },
      });
    } else {
      onClose?.();
    }
  };

  const handleAddPlot = () => {
    if (formRef.current && !isEqual(formRef.current.initialValues, formRef.current.values)) {
      openDialog({
        type: 'ALERT',
        props: {
          title: 'Unsaved changes',
          text: 'Are you sure you want to close this window? All unsaved changes will be lost and you will not be able to undo this action.',
          submitText: 'Discard changes',
          itemTitle: farm.title,
          displayCloseButton: true,
          onSubmit: () => {
            onClose?.();
            openDialog({
              type: 'ADD_EDIT_FARM_PLOT',
              props: {
                geoDataId,
                farmId: farm.id,
              },
            });
          },
          onCancel: () => undefined,
        },
      });
    } else {
      onClose?.();
      openDialog({
        type: 'ADD_EDIT_FARM_PLOT',
        props: {
          geoDataId,
          farmId: farm.id,
        },
      });
    }
  };

  const handleEditFarm = useCallback(
    (farmId: string) => {
      // Make sure the map is not in fullscreen mode anymore
      customMapRef.current?.exitFullscreen();

      // Close all open popups
      closeDialog();
      setInfoPopup(undefined);

      const farm: IFarmSite | undefined = neighbouringPlots.find(
        ({ farm }) => farm.id === farmId
      )?.farm;
      if (!farm) {
        throw new Error('Could not find farm');
      }
      openDialog({
        type: 'ADD_EDIT_SITE',
        props: { site: farm },
      });
    },
    [neighbouringPlots, openDialog, closeDialog]
  );

  const handleEditFarmPlot = useCallback(
    (plot: IPlot) => {
      // Make sure the map is not in fullscreen mode anymore
      customMapRef.current?.exitFullscreen();

      // Close all open popups
      closeDialog();
      setInfoPopup(undefined);

      openDialog({
        type: 'ADD_EDIT_FARM_PLOT',
        props: {
          plot,
          geoDataId,
          onPlotsUpdated,
        },
      });
    },
    [geoDataId, onPlotsUpdated, openDialog, closeDialog]
  );

  const handleFeatureClick = useCallback(
    (featureProperties: IFeatureProperties) => {
      setInfoPopup(prev => {
        if (prev && prev.id === featureProperties.plot.id) {
          return undefined;
        }
        return {
          id: featureProperties.plot.id,
          coordinate: featureProperties.center,
          content: (
            <PlotInfoPopup
              infoItem={featureProperties}
              onEditPlotClick={handleEditFarmPlot}
              onEditFarmClick={handleEditFarm}
            />
          ),
        };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [infoPopup, setInfoPopup]
  );

  const neighbouringLayerProps: GeoJsonLayerProps = useMemo(
    () => ({
      id: 'neighbouring-plots-map-layer',
      data: neighbouringPolygonFeatures,
      getLineWidth: 2,
      pickable: true,
      getPointRadius: 4,
      getFillColor: () => convertHexToRGBarray(theme.custom.themeColors.grayScale[40], 60),
      getLineColor: () => convertHexToRGBarray(theme.custom.themeColors.grayScale[40]),
      onClick: info => handleFeatureClick(info.object.properties),
    }), // eslint-disable-next-line react-hooks/exhaustive-deps
    [neighbouringPolygonFeatures, handleFeatureClick]
  );

  const handleDeleteAllPlots = async () => {
    await deletePlots({
      variables: { ids: plots.map(({ id }) => id) },
      onCompleted: () => {
        setSuccessMessage('Cultivation areas successfully deleted');
      },
      onError: () => {
        setErrorMessage('Failed to delete cultivation areas');
      },
    });
    onClose?.();
    onPlotsUpdated?.();
  };

  /**
   * Delete all plots
   */
  const handleDeleteCultivatedArea = async () => {
    openDialog({
      type: 'ALERT',
      props: {
        title: 'Delete all cultivation areas of',
        text: 'Are you sure you want to delete all cultivation areas? All data will be lost and you will not be able to recover the plots.',
        submitText: 'Delete',
        itemTitle: farm.title,
        displayCloseButton: true,
        onSubmit: handleDeleteAllPlots,
        onCancel: () => undefined,
      },
    });
  };

  /**
   * Handle all changes from the initial values to the new values of the form
   * All three mutation delete / update / create can be triggered here based on the changes
   */
  const handleSubmit = async (values: IPlotsValues) => {
    try {
      const newFeatures = values.featureCollection.features;

      const plotsToCreate = newFeatures.filter(({ id }) => !id);
      const plotsToUpdate = newFeatures.filter(({ geometry, id: plotId }) => {
        if (!plotId) return false;
        const prevPlot = plots.find(({ id }) => id === plotId);
        if (!prevPlot) return false;
        return !isEqual(geometry, omit(prevPlot.feature.geometry, '__typename'));
      }) as (Feature<Polygon | MultiPolygon | Point, { plotId: string }> & { id: string })[]; // All features without a plotId got filtered out

      const plotIdsToDelete = plots
        .filter(
          ({ id, externalId }) =>
            !newFeatures.some(
              ({ id: plotId, properties }) => id === plotId || properties?.plotId === externalId
            )
        )
        .map(({ id }) => id);

      const mutationArray = [];
      // Check if there are any plots to create, if not we don't need to call the createPlots mutation
      if (plotsToCreate.length > 0) {
        setErrorMessage(
          'Creating plots via AddEditFarmPlotsDialog is not supported. Please add plots individually via the farm plot dialog.'
        );
        captureException('Creating plots via AddEditFarmPlotsDialog is not supported.');
      }
      if (plotsToUpdate.length) {
        mutationArray.push(
          updatePlots({
            variables: {
              inputs: plotsToUpdate.map(({ geometry, id }) => ({
                id,
                // Can not automatically transform turf coordinates number[] into [number, number]
                geometry: geometry as TGeometry,
              })),
            },
          })
        );
      }
      if (plotIdsToDelete.length) {
        mutationArray.push(
          deletePlots({
            variables: { ids: plotIdsToDelete },
          })
        );
      }
      // Resolve all promises and check if all mutations were successful
      const result = await Promise.all(mutationArray);
      if (result) {
        setSuccessMessage('Cultivation areas successfully saved');
        onClose?.();
        onPlotsUpdated?.();
      }
    } catch (error) {
      console.error(error);
      setErrorMessage('An unknown error occurred while saving the cultivation areas.');
    } finally {
      formRef.current?.setSubmitting(false);
    }
  };

  return (
    <DialogDefault
      title="Edit cultivation area"
      itemTitle={farm.title}
      onClose={handleClose}
      open={open}
      fullWidth
      iconName="polygon-mapping"
      maxWidth="md"
    >
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        validateOnChange
        enableReinitialize
        validationSchema={plotsSchema}
        onSubmit={handleSubmit}
      >
        {({ values, isSubmitting, initialValues }) => (
          <Form style={{ width: '100%' }}>
            <StyledDialogContent>
              <TitleContainer>
                <Box display="flex" bgcolor="white" borderRadius="6px" padding="4px">
                  {!!farm.image ? (
                    <Image width={44} height={44} image={farm.image} />
                  ) : (
                    <Box display="flex" bgcolor="white" borderRadius="6px" padding="4px">
                      <Icon name="site-placeholder" size="xx-large" color="blue-ice" />
                    </Box>
                  )}
                </Box>

                <Box width="calc(100% - 60px)">
                  <FlexBox>
                    <ThemeTypography variant="ITEM_TITLE" autoOverflow>
                      {farm.title}
                    </ThemeTypography>
                    {farm.externalId && (
                      <Box ml={0.8} maxWidth="30%">
                        <ThemeTypography variant="BODY_MEDIUM" autoOverflow color="GRAY_80">
                          | {farm.externalId}
                        </ThemeTypography>
                      </Box>
                    )}
                  </FlexBox>

                  <FlexBox>
                    <Icon name="tag-filled" size="small" color="gray-60" />
                    <Box ml={0.5} />
                    <ThemeTypography variant="LABEL_INPUT">
                      {SiteTypeLabels[farm.siteType]}
                    </ThemeTypography>
                    <Box ml={1} />
                    {farm.siteType === ESiteType.FARM && (
                      <>
                        <Icon name="raw-material" size="small" color="gray-60" />
                        <Box ml={0.5} />
                        <ThemeTypography variant="LABEL_INPUT" autoOverflow>
                          {farm.rawMaterial?.title}
                        </ThemeTypography>
                        <Box ml={1} />
                      </>
                    )}
                    <FlexBox gap={0.5}>
                      <Icon name="location" size="small" color="gray-60" />
                      <Icon name="site-location-point" size="small" color="light-violet" />
                      <Box />
                      <ThemeTypography variant="LABEL_INPUT" autoOverflow maxWidth={200}>
                        {farm.locationName}
                      </ThemeTypography>
                    </FlexBox>
                  </FlexBox>
                </Box>
              </TitleContainer>

              <InfoContainer>
                <ThemeTypography variant="BODY_MEDIUM" color="GRAY_80">
                  Use this interface to edit all cultivation area plots of one farm.
                </ThemeTypography>
              </InfoContainer>

              {/* We can only show the map if all plots are loaded */}
              {loading ? (
                <Box height="100%" display="flex" flexDirection="column" gap={2}>
                  <Skeleton variant="rounded" width={250} height={26} />
                  <Skeleton variant="rounded" width={350} height={10} />
                  <Skeleton variant="rounded" width="100%" height={'calc(100% - 64px)'} />
                </Box>
              ) : (
                <Field
                  name="featureCollection"
                  viewOnly
                  infoPopup={infoPopup}
                  customMapRef={customMapRef}
                  geoJsonLayerLoading={neighbouringPlotsLoading}
                  geoJsonLayerProps={neighbouringLayerProps}
                  geoJsonLayerLabel="Show neighbouring farm plots on map (in grey)."
                  component={GeoDataField}
                  defaultView={defaultView}
                  marker={farm.locationCoordinates}
                  defaultCenter={{ ...farm.locationCoordinates }}
                  onMarkerPopupClose={() => setInfoPopup(undefined)}
                  onAddNewPlotClick={handleAddPlot}
                />
              )}
            </StyledDialogContent>

            <DialogActions>
              <FlexBox width="100%" justifyContent="space-between">
                {!!plots?.length ? (
                  <ThemeButton
                    color="BLUE_ICE"
                    size="large"
                    type="button"
                    disabled={loading || isSubmitting || updateLoading || neighbouringPlotsLoading}
                    loading={deleteLoading}
                    onClick={handleDeleteCultivatedArea}
                  >
                    Delete all plots
                  </ThemeButton>
                ) : (
                  <Box />
                )}

                <FlexBox gap={2}>
                  <ThemeButton color="BLUE_ICE" size="large" type="button" onClick={handleClose}>
                    Cancel
                  </ThemeButton>

                  <ThemeButton
                    data-testid="submit-activity-form-btn"
                    loading={updateLoading || isSubmitting}
                    color="PRIMARY"
                    size="large"
                    type="submit"
                    disabled={
                      loading ||
                      deleteLoading ||
                      neighbouringPlotsLoading ||
                      isEqual(values, initialValues)
                    }
                  >
                    Save
                  </ThemeButton>
                </FlexBox>
              </FlexBox>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </DialogDefault>
  );
};

export default AddEditFarmPlotsDialog;
